import { sentLabel } from "../access-rows.ts";
import type {
  InvitationInventory,
  PendingInvitation,
} from "../pending-invitations.ts";
import { exactTime, relativeUntil } from "../relative-time.ts";
import {
  QuietLine,
  QuietLoading,
  QuietRow,
  QuietRows,
  QuietSection,
} from "./quiet.tsx";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "./ui/alert-dialog.tsx";

/**
 * Hosted Team page: invitations sent but not yet accepted (render 13,
 * "Invited"). Rendered only for owners and administrators of a team whose
 * members AppNotes manages. An empty list renders nothing.
 */
export function PendingInvitationsSection({
  inventory,
  isPending,
  now,
  onResend,
  onRetry,
  onRevoke,
}: {
  readonly inventory: InvitationInventory;
  readonly isPending: (key: string) => boolean;
  readonly now?: number;
  readonly onResend: (invitation: PendingInvitation) => void;
  readonly onRetry: () => void;
  readonly onRevoke: (invitation: PendingInvitation) => void;
}) {
  if (inventory.status === "ready" && inventory.invitations.length === 0) {
    return null;
  }
  return (
    <QuietSection title="Invited" titleId="team-invitations-title">
      {inventory.status === "error" ? (
        <QuietLine
          action={
            <button className="text-action" onClick={onRetry} type="button">
              Retry
            </button>
          }
          tone="danger"
        >
          Invitations could not be loaded. {inventory.message}
        </QuietLine>
      ) : inventory.status === "loading" ? (
        <QuietLoading label="Loading invitations" />
      ) : (
        <QuietRows label="Invited">
          {inventory.invitations.map((invitation) => (
            <InvitationRow
              invitation={invitation}
              isPending={isPending}
              key={invitation.id}
              onResend={onResend}
              onRevoke={onRevoke}
              {...(now === undefined ? {} : { now })}
            />
          ))}
        </QuietRows>
      )}
    </QuietSection>
  );
}

function InvitationRow({
  invitation,
  isPending,
  now,
  onResend,
  onRevoke,
}: {
  readonly invitation: PendingInvitation;
  readonly isPending: (key: string) => boolean;
  readonly now?: number;
  readonly onResend: (invitation: PendingInvitation) => void;
  readonly onRevoke: (invitation: PendingInvitation) => void;
}) {
  const resending = isPending(`invitation:resend:${invitation.id}`);
  const revoking = isPending(`invitation:revoke:${invitation.id}`);
  const expires = relativeUntil(invitation.expires_at, now);
  return (
    <QuietRow
      data={{ "data-invitation-id": invitation.id }}
      actions={
        <>
          <button
            aria-label={`Resend invitation to ${invitation.email}`}
            className="text-action"
            disabled={resending || revoking}
            onClick={() => onResend(invitation)}
            type="button"
          >
            {resending ? "Resending…" : "Resend"}
          </button>
          <AlertDialog>
            <AlertDialogTrigger
              aria-label={`Cancel invitation to ${invitation.email}`}
              disabled={resending || revoking}
              render={<button className="text-action" type="button" />}
            >
              {revoking ? "Cancelling…" : "Cancel"}
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Cancel this invitation?</AlertDialogTitle>
                <AlertDialogDescription>
                  The link sent to {invitation.email} stops working. You can
                  invite them again later.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Keep it</AlertDialogCancel>
                <AlertDialogAction
                  disabled={revoking}
                  onClick={() => onRevoke(invitation)}
                  type="button"
                  variant="danger"
                >
                  {revoking ? "Cancelling…" : "Cancel invitation"}
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </>
      }
      detail={
        <time
          dateTime={invitation.created_at}
          title={exactTime(invitation.created_at)}
        >
          {sentLabel(invitation.created_at, now)}
        </time>
      }
      name={
        <>
          <span className="invite-email">{invitation.email}</span>
          {invitation.role === "admin" ? <small>Admin</small> : null}
        </>
      }
      title={[
        invitation.inviter === undefined
          ? null
          : `Invited by ${invitation.inviter.display_name}`,
        expires === "Expired"
          ? "Expired"
          : `Expires ${/^[A-Z][a-z]/u.test(expires) ? expires.charAt(0).toLowerCase() + expires.slice(1) : expires}`,
      ]
        .filter((value): value is string => value !== null)
        .join(". ")}
    />
  );
}
